import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import type { ReactNode } from 'react';

// ReadyState enum values
const WS_READY_STATE = {
  CONNECTING: 0,
  OPEN: 1,
  CLOSING: 2,
  CLOSED: 3,
  UNINSTANTIATED: -1,
} as const;

interface IWebSocketMessage {
  event: string;
  message?: any;
  payload?: {
    event: string;
    data?: any;
  };
  data?: any;
  targetId?: string;
}

interface WebSocketContextProps {
  sendJsonMessage: (message: object) => void;
  lastJsonMessage: IWebSocketMessage | null;
  readyState: number;
  isSocketConnected: boolean;
}

interface WebSocketProviderProps {
  children: ReactNode;
  userId?: string;
  driverId?: string;
  onDriverLocationUpdate?: (data: any) => void;
  onBookingStatusUpdate?: (data: any) => void;
  onDriverRouteUpdate?: (data: any) => void;
}

const WebSocketContext = createContext<WebSocketContextProps | undefined>(undefined);

// Socket events from backend
const SOCKET_EVENTS = {
  CONNECT: "connect",
  DISCONNECT: "disconnect",
  CONNECT_ERROR: "connect_error",
  BOOKING_STATUS_UPDATE: "job_update",
  DRIVER_LOCATION_UPDATE: "driverLocationUpdate",
  DRIVER_ROUTE_UPDATE: "routeProgressChange",
  TRIP_ADDRESS_UPDATE: "UpdatedTripAddress",
};

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  children,
  userId,
  driverId,
  onDriverLocationUpdate,
  onBookingStatusUpdate,
  onDriverRouteUpdate
}) => {

  const wsRef = useRef<WebSocket | null>(null);
  const [readyState, setReadyState] = useState<number>(WS_READY_STATE.UNINSTANTIATED);
  const [lastJsonMessage, setLastJsonMessage] = useState<IWebSocketMessage | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const maxReconnectAttempts = 10;
  const reconnectInterval = 3000;

  const getSocketUrl = () => {
    const apiUrl = String(process.env.BUN_PUBLIC_WEB_SOCKET_URL);
    const socketUrl = apiUrl?.replace('http', 'ws');
    const urlWithParams = userId ? `${apiUrl}/users?guestId=${userId}` : socketUrl;
    return urlWithParams;
  };

  const sendJsonMessage = useCallback((message: object) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket is not open. Cannot send message:', message);
    }
  }, []);

  const connect = useCallback(() => {

    if (!userId) {
      return;
    }

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      const url = getSocketUrl();


      const ws = new WebSocket(url);
      wsRef.current = ws;
      setReadyState(WS_READY_STATE.CONNECTING);

      ws.onopen = () => {


        setReadyState(WS_READY_STATE.OPEN);
        reconnectAttemptsRef.current = 0;

        // Register for user updates
        // if (userId) {
        //   sendJsonMessage({
        //     event: 'registerUser',
        //     data: {
        //       guestId: userId
        //     }
        //   });
        // }
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as IWebSocketMessage;
          setLastJsonMessage(message);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setReadyState(WS_READY_STATE.CLOSED);
      };

      ws.onclose = () => {

        setReadyState(WS_READY_STATE.CLOSED);

        // Attempt reconnection
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++;


          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, reconnectInterval);
        } else {
          console.error('Max reconnection attempts reached');
        }
      };
    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
      setReadyState(WS_READY_STATE.CLOSED);
    }
  }, [userId, sendJsonMessage]);

  // Connect on mount and when bookingId changes
  useEffect(() => {
    connect();

    // Cleanup on unmount
    // return () => {
    //   if (reconnectTimeoutRef.current) {
    //     clearTimeout(reconnectTimeoutRef.current);
    //   }
    //   if (wsRef.current) {
    //     wsRef.current.close();
    //     wsRef.current = null;
    //   }
    // };
  }, [connect]);

  const isSocketConnected = readyState === WS_READY_STATE.OPEN;



  useEffect(() => {
    if (lastJsonMessage) {
      const message = lastJsonMessage as IWebSocketMessage;

      switch (message?.payload?.event) {
        case SOCKET_EVENTS.CONNECT:
          break;

        case SOCKET_EVENTS.BOOKING_STATUS_UPDATE:

          if (onBookingStatusUpdate && message.payload.data) {
            onBookingStatusUpdate(message.payload.data.status);


            const durationRemaining = message.payload.data.driver.duration;
            const minutes = Math.floor(durationRemaining / 60);
            const seconds = Math.floor(durationRemaining % 60);
            const routeData = {
              // km: parseFloat((message.data.distanceRemaining / 1000).toFixed(1)),
              // kmText: `${(message.data.distanceRemaining / 1000).toFixed(1)} km`,
              durationText: `${minutes} min ${seconds} sec`,
              polyline: message?.payload?.data?.driver?.polyline,

            };
            if (onDriverRouteUpdate) {
              onDriverRouteUpdate(routeData);
            }


          }
          break;

        case SOCKET_EVENTS.DRIVER_LOCATION_UPDATE:

          if (onDriverLocationUpdate && message.payload.data) {

            const locationData = {
              driverId: message.targetId,
              location: {
                latitude: message.payload.data?.location?.latitude,
                longitude: message.payload.data?.location?.longitude,
              },

            };
            // if (locationData.driverId === driverId) {
            onDriverLocationUpdate(locationData);

            // }
          }
          break;

        case SOCKET_EVENTS.DRIVER_ROUTE_UPDATE:
          // alert("route update");
          if (onDriverRouteUpdate && message.data) {
            if (message.data?.durationRemaining > 0 && message.data?.distanceRemaining > 0) {
              const durationRemaining = message.data.durationRemaining;
              const minutes = Math.floor(durationRemaining / 60);
              const seconds = Math.floor(durationRemaining % 60);

              const routeData = {
                km: parseFloat((message.data.distanceRemaining / 1000).toFixed(1)),
                kmText: `${(message.data.distanceRemaining / 1000).toFixed(1)} km`,
                durationText: `${minutes} min ${seconds} sec`,
                duration: minutes,
                polyline: message.data.polyline,
              };
              onDriverRouteUpdate(routeData);

            }
          }
          break;

        case SOCKET_EVENTS.TRIP_ADDRESS_UPDATE:
          if (onBookingStatusUpdate && message.data) {
            onBookingStatusUpdate(message.data);
          }
          break;

        case SOCKET_EVENTS.DISCONNECT:

          break;

        case SOCKET_EVENTS.CONNECT_ERROR:
          console.error('Socket connection error');
          break;

        default:

          break;
      }
    }
  }, [lastJsonMessage, onDriverLocationUpdate, onBookingStatusUpdate, onDriverRouteUpdate]);

  return (
    <WebSocketContext.Provider value={{ sendJsonMessage, lastJsonMessage: lastJsonMessage as IWebSocketMessage | null, readyState, isSocketConnected }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocketContext = (): WebSocketContextProps | null => {
  const context = useContext(WebSocketContext);
  return context || null;
};



// 'use client'
// import 'leaflet/dist/leaflet.css';
// import L from "leaflet";
// import { useEffect, useState, useRef } from 'react';
// import "leaflet-curve";
// import * as polyline from '@mapbox/polyline';
// interface TripAddress {
//   markerType: string;
//   title: string;
//   address: string;
//   location: {
//     latitude: number;
//     longitude: number;
//   };
// }

// interface DriverLocation {
//   latitude: number;
//   longitude: number;
// }

// interface RideTrackingOSMapProps {
//   tripAddress?: TripAddress[];
//   driverLocation?: DriverLocation | null;
//   driverRouteString?: string; // Encoded polyline from backend (like mobile app)
//   showRoute?: boolean;
//   height?: string;
// }

// export default function RideTrackingOSMap({
//   tripAddress = [],
//   driverLocation = null,
//   driverRouteString = '',
//   showRoute = true,
//   height = '100%'
// }: RideTrackingOSMapProps) {
//   console.log("driverLocation:", driverRouteString);

//   const [mapState, setMap] = useState<any>(null);
//   const mapIdRef = useRef(`map-${Math.random().toString(36).substr(2, 9)}`);
//   const markersRef = useRef<any[]>([]);
//   const polylineRef = useRef<any>(null);
//   const driverMarkerRef = useRef<any>(null);
//   const [routeCoordinates, setRouteCoordinates] = useState<Array<{ latitude: number; longitude: number }>>([]);
//   const hasUserInteractedRef = useRef(false);
//   const isInitialFitRef = useRef(true);
//   const animationFrameRef = useRef<number | null>(null);
//   const previousDriverLocationRef = useRef<DriverLocation | null>(null);
//   const currentBearingRef = useRef<number>(0); // Store current bearing/rotation


//   const decodePolylineString = (polylineString: string): Array<{ latitude: number; longitude: number }> => {
//     if (!polylineString) return [];

//     try {
//       const decoded6 = polyline.decode(polylineString, 6);
//       if (decoded6 && decoded6.length > 0) {
//         return decoded6?.map((point: [number, number]) => ({
//           latitude: point[0],
//           longitude: point[1]
//         }));
//       }
//     } catch (error6) {
//       ////////console.log("Precision 6 failed, trying default precision");
//     }

//     try {
//       const decoded5 = polyline.decode(polylineString);
//       if (decoded5 && decoded5.length > 0) {
//         return decoded5?.map((point: [number, number]) => ({
//           latitude: point[0],
//           longitude: point[1]
//         }));
//       }
//     } catch (error5) {
//       ////////console.log("Polyline decoding failed for string:", polylineString);
//     }

//     return [];
//   };
//   // Decode driverRouteString when it changes (like mobile app)
//   useEffect(() => {
//     if (driverRouteString && driverRouteString.trim() !== '') {
//       const decoded = decodePolylineString(driverRouteString);

//       setRouteCoordinates(decoded);
//     } else {
//       // Fallback: use tripAddress coordinates if no encoded route
//       const coords = tripAddress
//         .filter(addr => addr?.location?.latitude && addr?.location?.longitude)
//         .map(addr => ({
//           latitude: addr.location.latitude,
//           longitude: addr.location.longitude
//         }));
//       setRouteCoordinates(coords);
//     }
//   }, [driverRouteString, JSON.stringify(tripAddress)]);

//   // Calculate bearing between two coordinates (returns degrees from north)
//   const calculateBearing = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
//     const toRadians = (deg: number) => deg * Math.PI / 180;
//     const toDegrees = (rad: number) => rad * 180 / Math.PI;

//     const dLng = toRadians(lng2 - lng1);
//     const lat1Rad = toRadians(lat1);
//     const lat2Rad = toRadians(lat2);

//     const y = Math.sin(dLng) * Math.cos(lat2Rad);
//     const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) -
//       Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLng);

//     const bearing = toDegrees(Math.atan2(y, x));
//     return (bearing + 360) % 360; // Normalize to 0-360
//   };

//   // Check if new location is a forward movement along the route
//   const isForwardMovement = (
//     prevLat: number,
//     prevLng: number,
//     newLat: number,
//     newLng: number
//   ): boolean => {
//     // If no route, assume forward movement
//     if (routeCoordinates.length === 0) return true;

//     // Find closest point on route to previous location
//     let minDistPrev = Infinity;
//     let prevRouteIndex = -1;

//     routeCoordinates.forEach((coord, index) => {
//       const dist = Math.sqrt(
//         Math.pow(coord.latitude - prevLat, 2) + Math.pow(coord.longitude - prevLng, 2)
//       );
//       if (dist < minDistPrev) {
//         minDistPrev = dist;
//         prevRouteIndex = index;
//       }
//     });

//     // Find closest point on route to new location
//     let minDistNew = Infinity;
//     let newRouteIndex = -1;

//     routeCoordinates.forEach((coord, index) => {
//       const dist = Math.sqrt(
//         Math.pow(coord.latitude - newLat, 2) + Math.pow(coord.longitude - newLng, 2)
//       );
//       if (dist < minDistNew) {
//         minDistNew = dist;
//         newRouteIndex = index;
//       }
//     });

//     // Allow forward movement (or slight backward within tolerance)
//     // This handles GPS inaccuracies
//     return newRouteIndex >= prevRouteIndex - 2;
//   };

//   // Smooth animation function for driver marker
//   const animateDriverMarker = (
//     startLat: number,
//     startLng: number,
//     endLat: number,
//     endLng: number,
//     bearing: number,
//     duration: number = 1000 // 1 second animation by default
//   ) => {
//     if (!driverMarkerRef.current) return;

//     const startTime = performance.now();
//     const startBearing = currentBearingRef.current;

//     // Calculate bearing difference and take shortest rotation path
//     let bearingDiff = bearing - startBearing;
//     if (bearingDiff > 180) bearingDiff -= 360;
//     if (bearingDiff < -180) bearingDiff += 360;

//     const animate = (currentTime: number) => {
//       const elapsed = currentTime - startTime;
//       const progress = Math.min(elapsed / duration, 1); // 0 to 1

//       // Easing function for smoother animation (ease-in-out)
//       const easeProgress = progress < 0.5
//         ? 2 * progress * progress
//         : 1 - Math.pow(-2 * progress + 2, 2) / 2;

//       // Interpolate between start and end positions
//       const currentLat = startLat + (endLat - startLat) * easeProgress;
//       const currentLng = startLng + (endLng - startLng) * easeProgress;

//       // Interpolate bearing (rotation)
//       const currentBearing = startBearing + bearingDiff * easeProgress;
//       currentBearingRef.current = currentBearing;

//       // Update marker position
//       driverMarkerRef.current.setLatLng([currentLat, currentLng]);

//       // Update rotation of car icon
//       const markerElement = driverMarkerRef.current.getElement();
//       if (markerElement) {
//         const carIcon = markerElement.querySelector('.driver-car-icon');
//         if (carIcon) {
//           (carIcon as HTMLElement).style.transform = `rotate(${currentBearing}deg)`;
//         }
//       }

//       // Continue animation if not complete
//       if (progress < 1) {
//         animationFrameRef.current = requestAnimationFrame(animate);
//       } else {
//         // Update previous location after animation completes
//         previousDriverLocationRef.current = { latitude: endLat, longitude: endLng };
//       }
//     };

//     // Cancel any ongoing animation
//     if (animationFrameRef.current) {
//       cancelAnimationFrame(animationFrameRef.current);
//     }

//     // Start animation
//     animationFrameRef.current = requestAnimationFrame(animate);
//   };

//   // Update driver marker position with smooth animation
//   useEffect(() => {
//     if (!mapState || !driverMarkerRef.current || !driverLocation) return;

//     const newLat = driverLocation.latitude;
//     const newLng = driverLocation.longitude;

//     if (!newLat || !newLng) return;

//     // If we have a previous location, animate to the new one
//     if (previousDriverLocationRef.current) {
//       const prevLat = previousDriverLocationRef.current.latitude;
//       const prevLng = previousDriverLocationRef.current.longitude;


//       const isForward = isForwardMovement(prevLat, prevLng, newLat, newLng);
//       if (!isForward) {
//         console.log('Skipping backward movement update');
//         return;
//       }

//       // Calculate distance to determine animation duration
//       const distance = Math.sqrt(
//         Math.pow(newLat - prevLat, 2) + Math.pow(newLng - prevLng, 2)
//       );

//       // Adjust duration based on distance (between 500ms and 2000ms)
//       const duration = Math.min(Math.max(distance * 100000, 500), 2000);

//       animateDriverMarker(prevLat, prevLng, newLat, newLng, duration);
//     } else {
//       // First update - no animation, just set position
//       driverMarkerRef.current.setLatLng([newLat, newLng]);
//       previousDriverLocationRef.current = { latitude: newLat, longitude: newLng };
//     }
//   }, [driverLocation, mapState]);

//   // Cleanup animation on unmount
//   useEffect(() => {
//     return () => {
//       if (animationFrameRef.current) {
//         cancelAnimationFrame(animationFrameRef.current);
//       }
//     };
//   }, []);

//   // Initialize map
//   useEffect(() => {
//     const mapElement = document.getElementById(mapIdRef.current);
//     if (!mapElement) return;

//     const map = L.map(mapIdRef.current, {
//       center: [40.8054, -74.0241],
//       zoom: 13,
//       scrollWheelZoom: true,
//       zoomControl: true,
//     });

//     // Add OpenStreetMap tile layer
//     L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
//       attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
//       maxZoom: 19,
//     }).addTo(map);

//     // Track user interactions (zoom, pan, drag)
//     map.on('zoomstart', () => {
//       hasUserInteractedRef.current = true;
//     });

//     map.on('movestart', () => {
//       hasUserInteractedRef.current = true;
//     });

//     setMap(map);

//     // Cleanup on component unmount
//     return () => {
//       map.remove();
//       setMap(null);
//     };
//   }, []);

//   // Update markers and route when tripAddress or driverLocation changes
//   useEffect(() => {
//     if (!mapState || !tripAddress || tripAddress.length === 0) return;

//     // Clear old markers
//     markersRef.current.forEach(marker => marker.remove());
//     markersRef.current = [];

//     // Clear old polyline
//     if (polylineRef.current) {
//       polylineRef.current.remove();
//       polylineRef.current = null;
//     }

//     // Don't clear driver marker here - we want to keep it for smooth animation
//     // Only clear it if driverLocation becomes null
//     if (!driverLocation && driverMarkerRef.current) {
//       driverMarkerRef.current.remove();
//       driverMarkerRef.current = null;
//       previousDriverLocationRef.current = null;
//     }

//     const bounds = L.latLngBounds([]);
//     const validAddresses = tripAddress.filter(
//       (addr) => addr?.location?.latitude && addr?.location?.longitude
//     );

//     if (validAddresses.length === 0) return;

//     // Add markers for each trip address
//     validAddresses.forEach((address, index) => {
//       const isPickup = index === 0;
//       const isDropoff = index === validAddresses.length - 1;
//       const isStop = !isPickup && !isDropoff;

//       // Create custom icon HTML
//       let markerColor = '#3b82f6'; // blue
//       let markerLabel = `${index}`;

//       if (isPickup) {
//         markerColor = '#10b981'; // green
//         markerLabel = 'P';
//       } else if (isDropoff) {
//         markerColor = '#ef4444'; // red
//         markerLabel = 'D';
//       }

//       const customIcon = L.divIcon({
//         className: 'custom-marker',
//         html: `
//           <div style="position: relative;">
//             <div style="
//               width: 36px;
//               height: 36px;
//               background-color: ${markerColor};
//               border-radius: 50%;
//               border: 3px solid white;
//               box-shadow: 0 2px 8px rgba(0,0,0,0.3);
//               display: flex;
//               align-items: center;
//               justify-content: center;
//               color: white;
//               font-weight: bold;
//               font-size: 14px;
//             ">
//               ${markerLabel}
//             </div>
//             <div style="
//               position: absolute;
//               top: 40px;
//               left: 50%;
//               transform: translateX(-50%);
//               background: white;
//               padding: 4px 8px;
//               border-radius: 4px;
//               box-shadow: 0 2px 4px rgba(0,0,0,0.2);
//               white-space: nowrap;
//               font-size: 11px;
//               font-weight: 500;
//               max-width: 150px;
//               overflow: hidden;
//               text-overflow: ellipsis;
//             ">
//               ${isPickup ? 'Pickup' : isDropoff ? 'Drop-off' : `Stop ${index}`}
//             </div>
//           </div>
//         `,
//         iconSize: [36, 60],
//         iconAnchor: [18, 18],
//       });

//       const marker = L.marker(
//         [address.location.latitude, address.location.longitude],
//         { icon: customIcon }
//       ).addTo(mapState);

//       marker.bindPopup(`
//         <div style="max-width: 200px;">
//           <strong>${address.title}</strong><br/>
//           <small>${address.address}</small>
//         </div>
//       `);

//       bounds.extend(marker.getLatLng());
//       markersRef.current.push(marker);
//     });

//     // Create driver marker if it doesn't exist and location is available
//     if (driverLocation && driverLocation.latitude && driverLocation.longitude && !driverMarkerRef.current) {
//       const driverIcon = L.divIcon({
//         className: 'driver-marker',
//         html: `
//           <div style="position: relative;">
//             <!-- Pulsing ring animation -->
//             <div style="
//               position: absolute;
//               width: 48px;
//               height: 48px;
//               background-color: rgba(59, 130, 246, 0.3);
//               border-radius: 50%;
//               animation: pulse 2s infinite;
//               top: 50%;
//               left: 50%;
//               transform: translate(-50%, -50%);
//             "></div>
//             <!-- Car icon -->
//             <div style="
//               width: 36px;
//               height: 36px;
//               background-color: #2563eb;
//               border-radius: 50%;
//               border: 2px solid white;
//               box-shadow: 0 4px 12px rgba(0,0,0,0.3);
//               display: flex;
//               align-items: center;
//               justify-content: center;
//               position: relative;
//               z-index: 10;
//             ">
//               <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
//                 <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
//               </svg>
//             </div>
//           </div>
//           <style>
//             @keyframes pulse {
//               0% {
//                 transform: translate(-50%, -50%) scale(1);
//                 opacity: 0.7;
//               }
//               50% {
//                 transform: translate(-50%, -50%) scale(1.3);
//                 opacity: 0.3;
//               }
//               100% {
//                 transform: translate(-50%, -50%) scale(1);
//                 opacity: 0.7;
//               }
//             }
//           </style>
//         `,
//         iconSize: [48, 48],
//         iconAnchor: [24, 24],
//       });

//       driverMarkerRef.current = L.marker(
//         [driverLocation.latitude, driverLocation.longitude],
//         { icon: driverIcon }
//       ).addTo(mapState);

//       driverMarkerRef.current.bindPopup('<strong>Driver Location</strong>');
//       previousDriverLocationRef.current = {
//         latitude: driverLocation.latitude,
//         longitude: driverLocation.longitude
//       };
//       bounds.extend(driverMarkerRef.current.getLatLng());
//     }

//     // Add driver marker to bounds if it exists
//     if (driverMarkerRef.current) {
//       bounds.extend(driverMarkerRef.current.getLatLng());
//     }

//     // Draw route line if enabled
//     if (showRoute && routeCoordinates.length >= 2) {
//       // Use decoded polyline coordinates (like mobile app's TrackingMap)
//       const polylineCoords: [number, number][] = routeCoordinates.map((coord) => [
//         coord.latitude,
//         coord.longitude,
//       ] as [number, number]);


//       polylineRef.current = L.polyline(polylineCoords, {
//         color: '#3b82f6',
//         weight: 4,
//         opacity: 0.8,
//         lineCap: 'round',
//         lineJoin: 'round',
//       }).addTo(mapState);

//       // Add route coordinates to bounds
//       polylineCoords.forEach(coord => {
//         bounds.extend([coord[0], coord[1]]);
//       });
//     } else if (showRoute && validAddresses.length >= 2) {
//       // Fallback: draw straight line between markers if no route data
//       const polylineCoords: [number, number][] = validAddresses.map((addr) => [
//         addr.location.latitude,
//         addr.location.longitude,
//       ] as [number, number]);

//       polylineRef.current = L.polyline(polylineCoords, {
//         color: '#3b82f6',
//         weight: 4,
//         opacity: 0.8,
//         lineCap: 'round',
//         lineJoin: 'round',
//         dashArray: '10, 10', // Dashed line to indicate it's not actual route
//       }).addTo(mapState);
//     }

//     // Fit map to bounds only on initial load or if user hasn't interacted
//     if (bounds.isValid() && !hasUserInteractedRef.current && isInitialFitRef.current) {
//       mapState.fitBounds(bounds, {
//         padding: [50, 50],
//         maxZoom: 16,
//       });
//       isInitialFitRef.current = false;
//     }
//   }, [tripAddress, mapState, showRoute, routeCoordinates]);

//   return (
//     <div style={{ position: 'relative', width: '100%', height }}>
//       <div id={mapIdRef.current} style={{ height: '100%', width: '100%', zIndex: 0 }} />


//     </div>
//   );
// }
